/**
 * Product theme registry — remaps brand CSS variables only.
 * Surfaces / status colors stay as defined in `index.css`.
 * Default (`pace-teal`) clears overrides so `:root` / `.dark` remain source of truth.
 *
 * Shell chrome (`--pace-shell-brand*`) is derived from `--ds-primary` in index.css,
 * so left rail + secondary nav follow automatically. Legacy `redhalo-*` Tailwind
 * brand keys also map to `--ds-*` in tailwind.config.js.
 */

export const PRODUCT_THEME_STORAGE_KEY = "pacehrm-product-theme";
export const PRODUCT_THEME_CHANGED_EVENT = "pacehrm-product-theme-changed";
export const DEFAULT_PRODUCT_THEME_ID = "pace-teal";

/** Brand + ring tokens we may override (matches roles already used across the app). */
export const THEME_VAR_KEYS = [
  "--ds-primary",
  "--ds-primary-hover",
  "--ds-primary-fg",
  "--ds-secondary",
  "--ds-secondary-hover",
  "--ds-secondary-fg",
  "--ds-accent",
  "--ds-accent-fg",
  "--ds-ring",
  "--primary",
  "--primary-foreground",
  "--secondary",
  "--secondary-foreground",
  "--accent",
  "--accent-foreground",
  "--ring",
];

/**
 * @typedef {Record<string, string>} ThemeVarMap
 * @typedef {{
 *   id: string,
 *   name: string,
 *   description: string,
 *   isDefault?: boolean,
 *   swatch: { primary: string, accent: string, secondary: string },
 *   light: ThemeVarMap | null,
 *   dark: ThemeVarMap | null,
 * }} ProductTheme
 */

/** Build light + dark brand maps from HSL channel triples (no `hsl()` wrapper). */
function brandMaps({
  primary,
  primaryHover,
  secondary,
  secondaryHover,
  accent,
  darkPrimary,
  darkPrimaryHover,
  darkSecondary,
  darkAccent,
}) {
  const light = {
    "--ds-primary": primary,
    "--ds-primary-hover": primaryHover,
    "--ds-primary-fg": "0 0% 100%",
    "--ds-secondary": secondary,
    "--ds-secondary-hover": secondaryHover,
    "--ds-secondary-fg": "0 0% 100%",
    "--ds-accent": accent,
    "--ds-accent-fg": secondary,
    "--ds-ring": `${primary} / 0.35`,
    "--primary": primary,
    "--primary-foreground": "0 0% 100%",
    "--secondary": secondary,
    "--secondary-foreground": "0 0% 100%",
    "--accent": accent,
    "--accent-foreground": secondary,
    "--ring": primary,
  };
  const dark = {
    "--ds-primary": darkPrimary,
    "--ds-primary-hover": darkPrimaryHover,
    "--ds-primary-fg": "0 0% 100%",
    "--ds-secondary": darkSecondary,
    "--ds-secondary-hover": darkSecondary,
    "--ds-secondary-fg": "222 47% 11%",
    "--ds-accent": darkAccent,
    "--ds-accent-fg": "210 40% 98%",
    "--ds-ring": `${darkPrimary} / 0.4`,
    "--primary": darkPrimary,
    "--primary-foreground": "0 0% 100%",
    "--secondary": darkAccent,
    "--secondary-foreground": "210 40% 98%",
    "--accent": darkAccent,
    "--accent-foreground": "210 40% 98%",
    "--ring": darkPrimary,
  };
  return { light, dark };
}

/**
 * Exactly 10 product themes (including Pace Teal default).
 * Mix of warm + vibe: teal, sand, coral, amber, rose, blue, purple, black, olive, indigo.
 * @type {ProductTheme[]}
 */
export const PRODUCT_THEMES = [
  {
    id: "pace-teal",
    name: "Pace Teal",
    description: "Current product default",
    isDefault: true,
    swatch: {
      primary: "#165b5d",
      accent: "#d9e9ea",
      secondary: "#122d4f",
    },
    light: null,
    dark: null,
  },
  {
    id: "warm-sand",
    name: "Warm Sand",
    description: "Soft clay beige",
    swatch: {
      primary: "#99704e",
      accent: "#f3e8dc",
      secondary: "#3d2e24",
    },
    ...brandMaps({
      primary: "28 42% 42%",
      primaryHover: "28 45% 34%",
      secondary: "25 30% 22%",
      secondaryHover: "25 32% 28%",
      accent: "32 45% 92%",
      darkPrimary: "28 38% 52%",
      darkPrimaryHover: "28 40% 58%",
      darkSecondary: "28 25% 72%",
      darkAccent: "28 20% 22%",
    }),
  },
  {
    id: "soft-coral",
    name: "Soft Coral",
    description: "Peachy warm coral",
    swatch: {
      primary: "#c25a45",
      accent: "#f8e6e1",
      secondary: "#4a2c24",
    },
    ...brandMaps({
      primary: "12 55% 48%",
      primaryHover: "12 58% 40%",
      secondary: "15 35% 24%",
      secondaryHover: "15 36% 30%",
      accent: "14 55% 93%",
      darkPrimary: "12 48% 55%",
      darkPrimaryHover: "12 50% 60%",
      darkSecondary: "14 30% 74%",
      darkAccent: "12 22% 22%",
    }),
  },
  {
    id: "honey-amber",
    name: "Honey Amber",
    description: "Golden warm amber",
    swatch: {
      primary: "#b67a20",
      accent: "#f8ecd4",
      secondary: "#3f2e16",
    },
    ...brandMaps({
      primary: "36 70% 42%",
      primaryHover: "36 72% 34%",
      secondary: "30 40% 22%",
      secondaryHover: "30 42% 28%",
      accent: "40 70% 92%",
      darkPrimary: "36 60% 52%",
      darkPrimaryHover: "36 62% 58%",
      darkSecondary: "36 35% 72%",
      darkAccent: "36 22% 22%",
    }),
  },
  {
    id: "dusty-rose",
    name: "Dusty Rose",
    description: "Muted warm rose",
    swatch: {
      primary: "#a65d6b",
      accent: "#f5e6ea",
      secondary: "#3f2430",
    },
    ...brandMaps({
      primary: "350 35% 48%",
      primaryHover: "350 38% 40%",
      secondary: "345 28% 24%",
      secondaryHover: "345 30% 30%",
      accent: "350 40% 94%",
      darkPrimary: "350 32% 58%",
      darkPrimaryHover: "350 34% 64%",
      darkSecondary: "350 25% 74%",
      darkAccent: "350 18% 22%",
    }),
  },
  {
    id: "ocean-blue",
    name: "Ocean Blue",
    description: "Clear calm blue",
    swatch: {
      primary: "#2563eb",
      accent: "#dbeafe",
      secondary: "#1e3a5f",
    },
    ...brandMaps({
      primary: "217 91% 53%",
      primaryHover: "217 91% 45%",
      secondary: "214 52% 25%",
      secondaryHover: "214 52% 32%",
      accent: "214 95% 93%",
      darkPrimary: "217 91% 60%",
      darkPrimaryHover: "217 91% 66%",
      darkSecondary: "214 40% 75%",
      darkAccent: "217 30% 22%",
    }),
  },
  {
    id: "violet-purple",
    name: "Violet Purple",
    description: "Soft vivid purple",
    swatch: {
      primary: "#7c3aed",
      accent: "#ede9fe",
      secondary: "#3b1d6e",
    },
    ...brandMaps({
      primary: "262 83% 58%",
      primaryHover: "262 83% 50%",
      secondary: "263 58% 27%",
      secondaryHover: "263 58% 34%",
      accent: "252 94% 95%",
      darkPrimary: "262 76% 64%",
      darkPrimaryHover: "262 76% 70%",
      darkSecondary: "262 40% 78%",
      darkAccent: "262 28% 22%",
    }),
  },
  {
    id: "midnight-black",
    name: "Midnight Black",
    description: "Near-black charcoal",
    swatch: {
      primary: "#1c1917",
      accent: "#e7e5e4",
      secondary: "#0c0a09",
    },
    ...brandMaps({
      primary: "24 10% 10%",
      primaryHover: "24 10% 6%",
      secondary: "24 10% 4%",
      secondaryHover: "24 10% 12%",
      accent: "24 6% 90%",
      darkPrimary: "24 5% 55%",
      darkPrimaryHover: "24 5% 62%",
      darkSecondary: "24 5% 72%",
      darkAccent: "24 6% 18%",
    }),
  },
  {
    id: "warm-olive",
    name: "Warm Olive",
    description: "Earthy muted olive",
    swatch: {
      primary: "#6b7342",
      accent: "#eef0e0",
      secondary: "#2f321f",
    },
    ...brandMaps({
      primary: "72 28% 36%",
      primaryHover: "72 30% 28%",
      secondary: "70 22% 20%",
      secondaryHover: "70 24% 26%",
      accent: "70 30% 92%",
      darkPrimary: "72 24% 48%",
      darkPrimaryHover: "72 26% 54%",
      darkSecondary: "72 18% 70%",
      darkAccent: "72 14% 20%",
    }),
  },
  {
    id: "indigo-vibe",
    name: "Indigo Vibe",
    description: "Deep indigo energy",
    swatch: {
      primary: "#4338ca",
      accent: "#e0e7ff",
      secondary: "#1e1b4b",
    },
    ...brandMaps({
      primary: "243 75% 51%",
      primaryHover: "243 75% 42%",
      secondary: "244 47% 20%",
      secondaryHover: "244 47% 28%",
      accent: "226 100% 94%",
      darkPrimary: "239 84% 67%",
      darkPrimaryHover: "239 84% 72%",
      darkSecondary: "234 40% 78%",
      darkAccent: "243 30% 22%",
    }),
  },
];

export function getProductThemeById(id) {
  return (
    PRODUCT_THEMES.find((t) => t.id === id) ||
    PRODUCT_THEMES.find((t) => t.isDefault) ||
    PRODUCT_THEMES[0]
  );
}

export function readStoredProductThemeId() {
  try {
    const raw = localStorage.getItem(PRODUCT_THEME_STORAGE_KEY);
    if (!raw) return DEFAULT_PRODUCT_THEME_ID;
    if (PRODUCT_THEMES.some((t) => t.id === raw)) return raw;
  } catch {
    /* ignore */
  }
  return DEFAULT_PRODUCT_THEME_ID;
}

function clearThemeVars(root) {
  THEME_VAR_KEYS.forEach((key) => {
    root.style.removeProperty(key);
  });
}

function writeThemeVars(root, map) {
  if (!map) {
    clearThemeVars(root);
    return;
  }
  THEME_VAR_KEYS.forEach((key) => {
    if (map[key] != null) root.style.setProperty(key, map[key]);
    else root.style.removeProperty(key);
  });
}

function isDarkModeActive() {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

/**
 * Apply a product theme by remapping brand CSS variables on <html>.
 * Default theme removes overrides so index.css remains authoritative.
 */
export function applyProductTheme(themeId, { persist = false } = {}) {
  if (typeof document === "undefined") return DEFAULT_PRODUCT_THEME_ID;

  const theme = getProductThemeById(themeId);
  const root = document.documentElement;
  const useDark = isDarkModeActive();
  const map = useDark ? theme.dark : theme.light;

  writeThemeVars(root, map);
  root.setAttribute("data-product-theme", theme.id);

  if (persist) {
    try {
      localStorage.setItem(PRODUCT_THEME_STORAGE_KEY, theme.id);
    } catch {
      /* ignore */
    }
  }

  try {
    window.dispatchEvent(
      new CustomEvent(PRODUCT_THEME_CHANGED_EVENT, {
        detail: { themeId: theme.id },
      })
    );
  } catch {
    /* ignore */
  }

  return theme.id;
}

/** Call once before React mounts (and whenever dark class toggles). */
export function initProductTheme() {
  return applyProductTheme(readStoredProductThemeId(), { persist: false });
}

/** Re-apply stored theme after dark/light class changes. */
export function refreshProductThemeForColorScheme() {
  return applyProductTheme(readStoredProductThemeId(), { persist: false });
}
